import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import { Button } from "@/shared/foundation/primitives/Button";

import { FieldTypeIcon } from "./fieldTypeIcons";
import type { FilterDefinition } from "./FilterDefinition";
import styles from "./FilterForm.module.css";
import {
  type Filter,
  filterSchema,
  getDefaultsForVariant,
  isValueless,
} from "./FilterSchema";
import { OperatorPicker } from "./OperatorPicker";
import { BooleanValueInput } from "./valueInputs/BooleanValueInput";
import { DateValueInput } from "./valueInputs/DateValueInput";
import { EnumValueInput } from "./valueInputs/EnumValueInput";
import { NumericValueInput } from "./valueInputs/NumericValueInput";
import { StringValueInput } from "./valueInputs/StringValueInput";

interface FilterFormProps {
  /** The field the user picked in the left pane. */
  selectedField: FilterDefinition;

  /** Fired when the user clicks Add filter (or Cmd/Ctrl+Enter on a non-text input). */
  onCommit: (filter: Filter) => void;

  /** Fired when the user clicks Cancel. */
  onCancel: () => void;
}

/**
 * The right pane of the filter menu — and the entire form.
 */
export function FilterForm({
  selectedField,
  onCommit,
  onCancel,
}: FilterFormProps) {
  const form = useForm<Filter>({
    resolver: zodResolver(filterSchema),
    defaultValues: getDefaultsForVariant(selectedField),
    // Continuous validation so the Add filter button reflects validity
    // live as the user fills the value input(s).
    mode: "onChange",
  });

  const filter = form.watch();

  const setValues = (next: Filter["values"]) =>
    form.setValue("values", next, {
      shouldValidate: true,
      shouldDirty: true,
    });

  return (
    <form
      // No filter fields need browser provided autocomplete
      autoComplete="off"
      className={styles.panel}
      onSubmit={(event) => {
        void form.handleSubmit(onCommit)(event);
      }}
    >
      <section className={styles.content}>
        <FilterFormHeader field={selectedField} />
        <section className={styles.section} aria-label="Operator">
          <h4 className={styles.sectionLabel}>Operator</h4>
          <Controller
            name="comparator"
            control={form.control}
            render={({ field }) => (
              <OperatorPicker
                fieldType={selectedField.type}
                value={field.value}
                onChange={(next) => {
                  field.onChange(next);
                  // Reset any existing values for the current operator
                  setValues([]);
                }}
              />
            )}
          />
        </section>
        {!isValueless(filter.comparator) && (
          <section className={styles.section} aria-label="Value">
            <h4 className={styles.sectionLabel}>Value</h4>
            {renderValueInput(filter, selectedField, setValues)}
          </section>
        )}
      </section>
      <div className={styles.footer}>
        <Button
          type="button"
          variant="text"
          size="small"
          onClick={onCancel}
          className={styles.footerAction}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="small"
          disabled={!form.formState.isValid}
          className={styles.footerAction}
        >
          Add filter
        </Button>
      </div>
    </form>
  );
}

function FilterFormHeader({ field }: { field: FilterDefinition }) {
  return (
    <header className={styles.header}>
      <div className={styles.headerGroup}>
        <FieldTypeIcon type={field.type} className={styles.headerIcon} />
        <h3 className={styles.headerName}>{field.name}</h3>
      </div>
      <span className={styles.headerType}>{field.type}</span>
    </header>
  );
}

function renderValueInput(
  filter: Filter,
  selectedField: FilterDefinition,
  onChange: (next: Filter["values"]) => void,
) {
  switch (filter.type) {
    case "string":
      return (
        <StringValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
        />
      );
    case "numeric":
      return (
        <NumericValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
        />
      );
    case "date":
      return (
        <DateValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
        />
      );
    case "boolean":
      return (
        <BooleanValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
        />
      );
    case "enum":
      if (selectedField.type !== "enum") {
        // Programming error.
        throw new Error(
          "Internal Error. SelectedField type and form field type diverged.",
        );
      }
      return (
        <EnumValueInput
          comparator={filter.comparator}
          value={filter.values}
          onChange={onChange}
          options={selectedField.options}
        />
      );
  }
}
